import { Select } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableEmptyRow, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useToast } from '@/components/ui/toast'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useUpdateAdCampaign } from '@/hooks/useAds'
import type { AdAccountDto, AdCampaignDto, AdVenueOptionDto } from '@lib/dto/ads.dto'
import { adPlaceKey, adPlaceLabel, adPlaceOptions, parseAdPlaceKey } from './adPlaces'

interface AdCampaignTableProps {
  campaigns: AdCampaignDto[]
  accounts: AdAccountDto[]
  venues: AdVenueOptionDto[]
}

/** The value the picker holds for a campaign following its account. */
const FOLLOWS = ''
/** The value for an own place whose venue was deleted: shown, never chosen. */
const DELETED = 'deleted'

/**
 * Each campaign of an account in use, and where it counts — its account's
 * place unless it has one of its own (docs/ad-analytics.spec.md, "Assigning
 * accounts and campaigns"). Changed in place: a new place takes the campaign's
 * whole history with it.
 */
export function AdCampaignTable({ campaigns, accounts, venues }: AdCampaignTableProps) {
  const update = useUpdateAdCampaign()
  const { toast } = useToast()
  const options = adPlaceOptions(venues)
  const accountsById = new Map(accounts.map((a) => [a.id, a]))

  function followsLabel(account: AdAccountDto | undefined): string {
    if (account?.defaultVenueId == null) return 'Follows the account'
    const place = adPlaceLabel({ venueId: account.defaultVenueId, trackId: account.defaultTrackId }, venues)
    return `Follows the account (${place ?? 'a deleted venue'})`
  }

  function valueOf(campaign: AdCampaignDto): string {
    if (campaign.ownVenueId === null) return FOLLOWS
    if (campaign.ownVenueDeleted) return DELETED
    return adPlaceKey({ venueId: campaign.ownVenueId, trackId: campaign.ownTrackId })
  }

  function choose(campaign: AdCampaignDto, value: string) {
    const place = value === FOLLOWS ? null : parseAdPlaceKey(value)
    if (value !== FOLLOWS && place === null) return
    update.mutate(
      { campaignId: campaign.id, input: { place } },
      { onError: (e) => toast(requestErrorMessage(e), { variant: 'error' }) },
    )
  }

  return (
    <Table heading={campaigns.length === 1 ? '1 campaign' : `${String(campaigns.length)} campaigns`}>
      <TableHeader>
        <TableHead>Campaign</TableHead>
        <TableHead>Account</TableHead>
        <TableHead>Counts toward</TableHead>
      </TableHeader>
      <TableBody>
        {campaigns.length === 0 ? (
          <TableEmptyRow colSpan={3}>Campaigns appear here once an account in use has brought in its history.</TableEmptyRow>
        ) : null}
        {campaigns.map((campaign) => {
          const account = accountsById.get(campaign.accountId)
          return (
            <TableRow key={campaign.id}>
              <TableCell className="min-w-48 font-medium text-ink">{campaign.name}</TableCell>
              <TableCell className="min-w-40 text-ink-muted">{account?.name ?? '—'}</TableCell>
              <TableCell className="min-w-64">
                <Select
                  size="sm"
                  aria-label={`Where ${campaign.name} counts`}
                  value={valueOf(campaign)}
                  disabled={update.isPending}
                  onChange={(e) => choose(campaign, e.target.value)}
                >
                  <option value={FOLLOWS}>{followsLabel(account)}</option>
                  {campaign.ownVenueDeleted ? (
                    <option value={DELETED} disabled>
                      A deleted venue — choose another
                    </option>
                  ) : null}
                  {options.map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}
